/* eslint-disable jsx-a11y/anchor-is-valid */
import { Box, Link, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronRightLogo from '@mui/icons-material/ChevronRightRounded';
import ExternalLinkLogo from '@mui/icons-material/OpenInNewRounded';
import { Navigation } from '../Header';
import { TypographyVariants } from '../../../theme/TypographyVariants';

interface BodySecondLevelMenuProps {
    setSubMenuClicked: (subMenuClicked: boolean) => void;
    setMainMenuClosed: (mainMenuClose: boolean) => void;
    onCloseClick: (mobileMenuClicked: boolean) => void;
    setSecondLevelSubMenuClicked: (secondLevelSubMenuClicked: boolean) => void;
    setSecondLevelSubMenuItem: (secondLevelSubMenuItem: string) => void;
    setThirdLevelSubMenuClicked: (thirdLevelSubMenuClicked: boolean) => void;
    setThirdLevelSubMenuItem: (thirdLevelSubMenuItem: string) => void;
    subMenuItem: string;
    secondLevelSubMenuClicked: boolean;
    secondLevelSubMenuItem: string;
    thirdLevelSubMenuClicked: boolean;
    thirdLevelSubMenuItem: string;
    navobj?: Navigation;
}

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.up(912)]: {
            paddingBottom: '1.875rem',
            marginRight: '1.5rem',
        },
        [theme.breakpoints.down(912)]: {
            height: '100vh',
            justifyContent: 'space-between',
            width: '100%',
        },
        [theme.breakpoints.only('tablet')]: {
            height: '100vh',
            justifyContent: 'space-between',
            width: '100%',
        },
    },
    menu: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down(912)]: {
            marginTop: '1.938rem',
            marginRight: '1.375rem',
            marginLeft: '1rem',
        },
        [theme.breakpoints.only('tablet')]: {
            marginTop: '2.438rem',
            marginRight: '2.375rem',
            marginLeft: '2rem',
        },
    },
    subMenu: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    item: {
        display: 'flex',
        marginTop: '0.5rem',
        [theme.breakpoints.up(912)]: {
            height: '44px',
        },
        [theme.breakpoints.down(912)]: {
            marginTop: '1.5rem',
        },
        [theme.breakpoints.only('tablet')]: {
            marginTop: '1.5rem',
        },
    },
    text: {
        display: 'flex',
        width: '361.6666px',
        justifyContent: 'space-between',
        marginRight: '0.625rem',
        [theme.breakpoints.down(912)]: {
            width: '100%',
            color: theme.palette.neutral1,
            alignItems: 'center',
        },
        [theme.breakpoints.only('tablet')]: {
            width: '100%',
            color: theme.palette.neutral1,
            alignItems: 'center',
        },
    },
    navContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '19.188rem',
        justifyContent: 'flex-start',
    },
    close: {
        height: '24px !important',
        width: '24px !imporant',
    },
}));

const BodySecondLevelMenu: React.FC<BodySecondLevelMenuProps> = (props: BodySecondLevelMenuProps) => {
    const theme = useTheme();
    const classes = useStyles();
    const {
        setSubMenuClicked,
        setSecondLevelSubMenuClicked,
        setSecondLevelSubMenuItem,
        setThirdLevelSubMenuClicked,
        setThirdLevelSubMenuItem,
        subMenuItem,
        navobj,
        secondLevelSubMenuClicked,
        secondLevelSubMenuItem,
        thirdLevelSubMenuClicked,
        thirdLevelSubMenuItem,
    } = props;

    return (
        <>
            {!secondLevelSubMenuClicked && !thirdLevelSubMenuClicked && (
                <Box className={classes.container}>
                    <Box className={classes.menu}>
                        <Box className={classes.subMenu}>
                            <Box className={classes.navContainer}>
                                <ArrowBackIcon
                                    titleAccess="back"
                                    className={classes.close}
                                    sx={{
                                        marginRight: '0.75rem !important',
                                    }}
                                    onClick={() => setSubMenuClicked(false)}
                                />
                                <Typography variant={TypographyVariants.h4}>{subMenuItem}</Typography>
                            </Box>
                        </Box>
                        {navobj?.child?.map((secondLevelNavItem: Navigation) => {
                            return (
                                <Box key={secondLevelNavItem.navText} className={classes.item}>
                                    {secondLevelNavItem.child !== null || undefined ? (
                                        // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                        <Link
                                            aria-label={secondLevelNavItem.altTextPath}
                                            className={classes.text}
                                            onClick={() => {
                                                setSecondLevelSubMenuClicked(true);
                                                setSecondLevelSubMenuItem(secondLevelNavItem.navText);
                                            }}
                                        >
                                            <Box>
                                                <Typography variant={TypographyVariants.bodyBold}>{secondLevelNavItem.navText}</Typography>
                                            </Box>
                                            <ChevronRightLogo sx={{ color: theme.palette.primary1 }} />
                                        </Link>
                                    ) : secondLevelNavItem.external ? (
                                        <Link aria-label={secondLevelNavItem.altTextPath} href={secondLevelNavItem.path} target="_blank" rel="noopener noreferrer" className={classes.text}>
                                            <Box>
                                                <Typography variant={TypographyVariants.bodyBold}>{secondLevelNavItem.navText}</Typography>
                                            </Box>
                                            <ExternalLinkLogo
                                                titleAccess={secondLevelNavItem.altTextIcon}
                                                sx={{
                                                    height: '16px',
                                                    filter: 'brightness(0) saturate(100%) invert(14%) sepia(6%) saturate(321%) hue-rotate(335deg) brightness(100%) contrast(93%)',
                                                }}
                                            />
                                        </Link>
                                    ) : (
                                        <Link aria-label={secondLevelNavItem.altTextPath} href={secondLevelNavItem.path} rel="noopener noreferrer" className={classes.text}>
                                            <Typography variant={TypographyVariants.bodyBold}>{secondLevelNavItem.navText}</Typography>
                                        </Link>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}
            {!thirdLevelSubMenuClicked && secondLevelSubMenuClicked && (
                <Box className={classes.container}>
                    <Box className={classes.menu}>
                        <Box className={classes.subMenu}>
                            <Box className={classes.navContainer}>
                                <ArrowBackIcon
                                    titleAccess="back"
                                    className={classes.close}
                                    sx={{
                                        marginRight: '0.75rem !important',
                                    }}
                                    onClick={() => {
                                        setSecondLevelSubMenuClicked(false);
                                        setThirdLevelSubMenuClicked(false);
                                    }}
                                />
                                <Typography variant={TypographyVariants.h4}>{secondLevelSubMenuItem}</Typography>
                            </Box>
                        </Box>
                        {navobj?.child
                            ?.find((thirdLevelObject: Navigation) => thirdLevelObject.navText === secondLevelSubMenuItem)
                            ?.child?.map((thirdLevelNavItem: Navigation) => {
                                return (
                                    <Box key={thirdLevelNavItem.navText} className={classes.item}>
                                        {thirdLevelNavItem.child !== null || undefined ? (
                                            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                                            <Link
                                                aria-label={thirdLevelNavItem.altTextPath}
                                                className={classes.text}
                                                onClick={() => {
                                                    setThirdLevelSubMenuClicked(true);
                                                    setThirdLevelSubMenuItem(thirdLevelNavItem.navText);
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant={TypographyVariants.bodyBold}>{thirdLevelNavItem.navText}</Typography>
                                                </Box>
                                                <ChevronRightLogo sx={{ color: theme.palette.primary1 }} />
                                            </Link>
                                        ) : thirdLevelNavItem.external ? (
                                            <Link aria-label={thirdLevelNavItem.altTextPath} href={thirdLevelNavItem.path} target="_blank" rel="noopener noreferrer" className={classes.text}>
                                                <Box>
                                                    <Typography variant={TypographyVariants.bodyBold}>{thirdLevelNavItem.navText}</Typography>
                                                </Box>
                                                <ExternalLinkLogo
                                                    titleAccess={thirdLevelNavItem.altTextIcon}
                                                    sx={{
                                                        height: '16px',
                                                        filter: 'brightness(0) saturate(100%) invert(14%) sepia(6%) saturate(321%) hue-rotate(335deg) brightness(100%) contrast(93%)',
                                                    }}
                                                />
                                            </Link>
                                        ) : (
                                            <Link aria-label={thirdLevelNavItem.altTextPath} href={thirdLevelNavItem.path} rel="noopener noreferrer" className={classes.text}>
                                                <Typography variant={TypographyVariants.bodyBold}>{thirdLevelNavItem.navText}</Typography>
                                            </Link>
                                        )}
                                    </Box>
                                );
                            })}
                    </Box>
                </Box>
            )}
            {thirdLevelSubMenuClicked && (
                <Box className={classes.container}>
                    <Box className={classes.menu}>
                        <Box className={classes.subMenu}>
                            <Box className={classes.navContainer}>
                                <ArrowBackIcon
                                    titleAccess="back"
                                    className={classes.close}
                                    sx={{
                                        marginRight: '0.75rem',
                                    }}
                                    onClick={() => {
                                        setThirdLevelSubMenuClicked(false);
                                    }}
                                />
                                <Typography variant={TypographyVariants.h4}>{thirdLevelSubMenuItem}</Typography>
                            </Box>
                        </Box>
                        <Box>
                            {navobj?.child
                                ?.find((thirdLevelObject: Navigation) => thirdLevelObject.navText === secondLevelSubMenuItem)
                                ?.child?.find((fourthLevelObject: Navigation) => fourthLevelObject.navText === thirdLevelSubMenuItem)
                                ?.child?.map((fourthLevelNavItem: Navigation) => {
                                    return (
                                        <Box key={fourthLevelNavItem.navText} className={classes.item}>
                                            <Link
                                                aria-label={fourthLevelNavItem.altTextPath}
                                                href={fourthLevelNavItem.path}
                                                target={fourthLevelNavItem.external ? '_blank' : '_self'}
                                                rel="noopener noreferrer"
                                                className={classes.text}
                                                sx={{ border: 'none', background: 'white' }}
                                            >
                                                <Typography variant={TypographyVariants.bodyBold}>{fourthLevelNavItem.navText}</Typography>
                                                {fourthLevelNavItem.external && <ExternalLinkLogo sx={{ height: '16px' }} />}
                                            </Link>
                                        </Box>
                                    );
                                })}
                        </Box>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default BodySecondLevelMenu;
